import {
	CSSProperties,
	ComponentProps,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	RefObject,
	useEffect,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';
import { useWindowSize } from 'usehooks-ts';

import { tokens } from '../theme/tokens';
import { Button } from './button/Button';
import { Callout } from './Callout';
import { Paragraph } from './Text';

const ZIndexFix = styled(Box)<{ $visible?: boolean; zIndex?: number }>`
	position: fixed;
	z-index: ${(props) => props.zIndex ?? 3};
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: ${(props) => (props.$visible ? 'auto' : 'none')};
`;

const Backdrop = styled(Box)<{ translucent?: boolean }>`
	position: fixed;
	z-index: 1;
	top: 0px;
	left: 0px;
	width: 100%;
	height: 100%;

	opacity: ${(props) => (props.translucent ? 0 : 0.5)};
	background: ${tokens.border.subtle};
`;

const PopoverContainer = styled(Box)<{
	animated?: boolean;
	maxHeight?: string;
	$elevation?: 'md' | 'lg';
	$borderless?: boolean;
	$inert?: boolean;
}>`
	position: fixed;
	display: flex;
	flex-direction: column;
	z-index: 1;
	background: ${tokens.surface.white};
	border: ${(props) => (props.$borderless ? 'none' : `1px solid ${tokens.border.default}`)};
	border-radius: ${tokens.radius.sm}px;
	box-shadow: ${(props) => tokens.shadow[props.$elevation ?? 'lg']};
	box-sizing: border-box;
	max-width: calc(100vw - 40px);
	max-height: ${(props) => props.maxHeight ?? 'min(calc(100dvh - 40px), 720px)'};

	${(props) =>
		props.animated &&
		`transition: 0.4s cubic-bezier(0, 1, 0.25, 1) transform, 0.4s cubic-bezier(0, 1, 0, 1) opacity;`}

	${(props) =>
		props.$inert &&
		css`
			pointer-events: none;

			* {
				pointer-events: none !important;
			}
		`}
`;

export type OriginPosition = {
	top: number;
	left: number;
	width: number;
	right?: number;
	height: number;
};

function genie(
	origin: {
		top: number;
		left: number;
		width: number;
		height: number;
	},
	destination: {
		top: number;
		left: number;
		width: number;
		height: number;
	},
) {
	const scale = {
		x: origin.width / destination.width,
		y: origin.height / destination.height,
	};

	const translate = {
		x: origin.left + origin.width / 2 - destination.left - destination.width / 2,
		y: origin.top + origin.height / 2 - destination.top - destination.height / 2,
	};

	return ` translate(${translate.x}px, ${translate.y}px) scale(${scale.x}, ${scale.y})`;
}

export function Popover(
	props: PropsWithChildren<{
		onDismiss?: () => void;
		visible?: boolean;
		backdrop?: boolean;
		center?: boolean;
		/**
		 * Indicates a preference for horizontal alignment of the popover.
		 * If there is not sufficient space to align to the preferred side, the popover will align on the opposite side, or centered if neither side has sufficient space.
		 * Behavior defaults to 'left'.
		 */
		horizontalAlign?: 'left' | 'right';
		/**
		 * Whether to focus the first element in the popover when it is opened.
		 * Defaults to false.
		 */
		autofocus?: boolean;
		zoom?: boolean;
		origin?: OriginPosition;
		zIndex?: number;
		/**
		 * Overrides the default max-height (`min(calc(100dvh - 40px), 720px)`),
		 * e.g. to let tall modals use the full viewport height.
		 */
		maxHeight?: string;
		elevation?: 'md' | 'lg';
		borderless?: boolean;
		/**
		 * Gap in pixels between the trigger and the popover.
		 * Defaults to 5.
		 */
		offset?: number;
		/**
		 * The shell ignores the pointer, so a menu underneath stays hoverable.
		 * The card itself still receives it. Used for a submenu beside another menu.
		 */
		passthrough?: boolean;
	}>,
) {
	const ref = useRef<HTMLDivElement>(null);

	const [mounted, setMounted] = useState(false);

	const [size, setSize] = useState<{ width: number; height: number } | undefined>(undefined);

	const window = useWindowSize();

	const origin = props.origin ?? {
		left: window.width / 2,
		top: window.height / 2,
		width: 0,
		height: 0,
	};

	const offset = props.offset ?? 5;

	const bounds = {
		top: origin.top + origin.height + offset,
		left: origin.left,
	};

	let center = props.center;

	if (!center) {
		const fitsBelow = size && bounds.top + size.height <= window.height;
		const fitsAbove = size && origin.top - size.height - offset >= 20;

		if (!fitsBelow && fitsAbove) {
			bounds.top = origin.top - size.height - offset;
		} else if (!fitsBelow) {
			center = true;
		}

		const fitsRight = size && bounds.left + size.width <= window.width;
		const fitsLeft = size && origin.left + origin.width - size.width >= 20;
		const leftEdge = origin.left;
		const rightEdge = origin.left + origin.width - (size?.width ?? 0);

		if (props.horizontalAlign === 'right') {
			bounds.left = !fitsLeft && fitsRight ? leftEdge : rightEdge;
		} else {
			bounds.left = !fitsRight && fitsLeft ? rightEdge : leftEdge;
		}

		if (!fitsRight && !fitsLeft) {
			center = true;
		}
	}

	if (center) {
		bounds.top = (window.height - (size?.height ?? 0)) / 2;
		bounds.left = (window.width - (size?.width ?? 0)) / 2;
	}

	bounds.top = Math.max(bounds.top, 20);
	bounds.left = Math.max(bounds.left, 20);

	const isCurrentRef = !!ref.current;

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const observer = new ResizeObserver((entries) => {
			const size = entries[0].contentRect;
			setTimeout(() => setSize(size));
		});

		observer.observe(ref.current);

		return () => observer.disconnect();
	}, [isCurrentRef]);

	const isCurrentSize = Boolean(size);

	useEffect(() => {
		if (size) {
			setMounted(true);
		}
	}, [isCurrentSize]);

	useEffect(() => {
		if (props.autofocus && props.visible) {
			// Timeout to ensure the ref is mounted
			setTimeout(() => {
				const firstChild = ref.current?.children[0];

				if (firstChild && 'focus' in firstChild) {
					(firstChild as HTMLElement).focus();
				}
			}, 0);
		}
	}, [props.visible, props.autofocus]);

	useEffect(() => {
		const el = ref.current;
		if (!el) {
			return;
		}

		if (props.visible === false) {
			el.setAttribute('inert', '');
		} else {
			el.removeAttribute('inert');
		}
	}, [props.visible, isCurrentRef]);

	const animation: CSSProperties =
		mounted && props.visible
			? {}
			: props.zoom
				? {
						transform: 'scale(1.2, 1.2)',
						opacity: 0,
						pointerEvents: 'none',
					}
				: {
						transform: size
							? genie(origin, {
									...bounds,
									width: size.width,
									height: size.height,
								})
							: undefined,
						opacity: 0,
						pointerEvents: 'none',
					};

	const combinedStyle =
		props.origin?.right === 0
			? { ...bounds, right: props.origin.right, ...animation }
			: { ...bounds, ...animation };

	return (
		<ZIndexFix
			$visible={props.visible}
			zIndex={props.zIndex}
			style={props.passthrough ? { pointerEvents: 'none' } : undefined}
		>
			{props.visible && !props.passthrough && (
				<Backdrop
					translucent={!(props.center || props.backdrop) || !mounted || !props.visible}
					onClick={() => props.onDismiss?.()}
				/>
			)}
			<PopoverContainer
				style={props.passthrough ? { ...combinedStyle, pointerEvents: 'auto' } : combinedStyle}
				animated={mounted}
				maxHeight={props.maxHeight}
				$elevation={props.elevation}
				$borderless={props.borderless}
				$inert={props.visible === false}
			>
				<div
					ref={ref}
					style={{
						display: 'flex',
						flexGrow: 1,
						flexShrink: 1,
						overflow: 'hidden',
						borderRadius: 'inherit',
					}}
				>
					{props.children}
				</div>
			</PopoverContainer>
		</ZIndexFix>
	);
}

export function GeometryObserver(props: {
	innerRef: RefObject<HTMLElement>;
	scroll?: boolean;
	children: (bounds: { top: number; left: number; width: number; height: number }) => ReactElement;
}) {
	const { innerRef, scroll } = props;
	const [bounds, setBounds] = useState<
		{ top: number; left: number; width: number; height: number } | undefined
	>(undefined);
	const lastBoundsRef = useRef<
		{ top: number; left: number; width: number; height: number } | undefined
	>(undefined);

	useEffect(() => {
		const element = innerRef.current;
		if (!element) {
			return;
		}

		const measure = () => {
			const rect = element.getBoundingClientRect();
			const top = rect.top + (scroll === false ? window.scrollY : 0);
			const left = rect.left + (scroll === false ? window.scrollX : 0);
			const next = { top, left, width: rect.width, height: rect.height };
			const prev = lastBoundsRef.current;
			if (
				prev &&
				prev.top === next.top &&
				prev.left === next.left &&
				prev.width === next.width &&
				prev.height === next.height
			) {
				return;
			}
			lastBoundsRef.current = next;
			setBounds(next);
		};

		measure();

		const observer = new ResizeObserver(() => {
			requestAnimationFrame(measure);
		});
		observer.observe(element);

		let rafId: number | null = null;
		const onResize = () => {
			if (rafId !== null) {
				return;
			}
			rafId = requestAnimationFrame(() => {
				rafId = null;
				measure();
			});
		};
		window.addEventListener('resize', onResize, { passive: true });
		window.addEventListener('scroll', onResize, { capture: true, passive: true });

		let frames = 0;
		let followFrame = requestAnimationFrame(function follow() {
			measure();
			frames += 1;
			if (frames < 60) {
				followFrame = requestAnimationFrame(follow);
			}
		});

		return () => {
			observer.disconnect();
			window.removeEventListener('resize', onResize);
			window.removeEventListener('scroll', onResize, true);
			cancelAnimationFrame(followFrame);
			if (rafId !== null) {
				cancelAnimationFrame(rafId);
			}
		};
	}, [innerRef, scroll]);

	if (bounds) {
		return props.children(bounds);
	} else {
		return null;
	}
}

type PopoverTargetPopoverProps = Omit<ComponentProps<typeof Popover>, 'origin'>;

type PopoverTargetProps = BoxProps &
	PropsWithChildren<
		{
			contents: ReactNode;
			style?: CSSProperties;
		} & PopoverTargetPopoverProps
	>;

function PopoverTarget({ children, contents, style, ...popoverProps }: PopoverTargetProps) {
	const ref = useRef(null);

	const [visible, setVisible] = useState(popoverProps.visible);
	const timer = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (timer.current) {
			clearTimeout(timer.current);
		}

		if (popoverProps.visible) {
			setVisible(true);
		} else {
			timer.current = setTimeout(() => setVisible(false), 1000);
		}
	}, [popoverProps.visible]);

	return (
		<>
			<Box ref={ref} style={style}>
				{children}
			</Box>
			{/* Portal to document.body so menus escape #root[inert] (e.g. open Drawer)
			    and can stack above the drawer via zIndex. */}
			{visible &&
				typeof document !== 'undefined' &&
				createPortal(
					<GeometryObserver innerRef={ref}>
						{(bounds) => (
							<Popover origin={bounds} {...popoverProps}>
								{contents}
							</Popover>
						)}
					</GeometryObserver>,
					document.body,
				)}
		</>
	);
}

Popover.Target = PopoverTarget;

function PopoverConfirm(
	props: PropsWithChildren<{
		visible: boolean;
		title: string;
		cancelTitle: string;
		confirmTitle: string;
		extraInfo?: string;
		onCancel: () => void;
		onConfirm: () => void;
		confirmType?: ComponentProps<typeof Button>['type'];
	}>,
) {
	return (
		<Popover.Target
			visible={props.visible}
			contents={
				<Box p={3}>
					<Paragraph mb={2} maxWidth={300}>
						{props.title}
					</Paragraph>
					{props.extraInfo && (
						<Callout color="blue" mb={3} mt={3} maxWidth={300}>
							{props.extraInfo}
						</Callout>
					)}

					<Flex>
						<Button type="secondary" small title={props.cancelTitle} onClick={props.onCancel} />

						<Box ml={2}>
							<Button
								small
								title={props.confirmTitle}
								onClick={props.onConfirm}
								type={props.confirmType}
							/>
						</Box>
					</Flex>
				</Box>
			}
			onDismiss={props.onCancel}
		>
			{props.children}
		</Popover.Target>
	);
}

Popover.Confirm = PopoverConfirm;
